// =============================================================================
// ABA "PMOC" de Cadastros (permissão "CRIAÇÃO DE OS SIMPLIFICADA").
//
// Uma coisa só: o CRONOGRAMA, e o botão que gera a OS preventiva. A OS nasce
// ligada ao plano (pmoc_os_vinculos), então ao ser concluída entra como "parada
// planejada" e NÃO conta como falha no MTTR/MTBF — quem conta é a OS corretiva.
//
// A tela lista por EQUIPAMENTO, não por atividade: o cronograma tem várias
// atividades para o mesmo aparelho e o usuário não deve ter que escolher qual.
// O nome da atividade continua indo na descrição da OS, montado no backend.
//
// O plano em si NASCE na ficha do cliente ("Gerar plano PMOC"), que é onde estão
// os equipamentos e ambientes que ele puxa. Não se cria plano aqui.
//
// Nada nesta tela joga o usuário para /app/pmoc: com esta permissão o módulo PMOC
// não está no menu, e sair para lá tiraria o usuário do contexto do cadastro —
// e era exatamente lá que "Gerar OS" vivia, ou seja, era inalcançável.
// =============================================================================
import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';

interface Programacao {
  id: number;
  ativo_id: number;
  plano_nome: string;
  atividade_nome: string;
  ativo_nome: string;
  ativo_status: string | null;
  ambiente_nome: string | null;
  proxima_execucao: string;
  data_limite: string | null;
  status: string;
  status_calculado: string;
  periodicidade_dias: number | string;
  norma_referencia: string | null;
  ultima_os_codigo: string | null;
  os_status: string | null;
  cliente_id: number | null;
  cliente_nome: string | null;
}

const ITENS_POR_PAGINA = 5;

const erroMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const dataBR = (v: string | null): string => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

export default function PmocConsulta() {
  const [programacoes, setProgramacoes] = useState<Programacao[]>([]);
  const [soPendentes, setSoPendentes] = useState(true);
  const [buscaEquipamento, setBuscaEquipamento] = useState('');
  const [pagina, setPagina] = useState(1);
  const [gerando, setGerando] = useState<number | null>(null);
  const [aberto, setAberto] = useState<number | null>(null);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [carregando, setCarregando] = useState(true);

  async function carregar() {
    setErro('');
    setCarregando(true);
    try {
      setProgramacoes(await api<Programacao[]>('/pmoc/programacoes.php?status=ativo'));
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { carregar(); }, []);

  // Gera a OS preventiva daquela programação.
  async function gerarOs(programacaoId: number) {
    setErro(''); setMsg('');
    setGerando(programacaoId);
    try {
      await api('/pmoc/gerar_os.php', { method: 'POST', body: { programacao_id: programacaoId } });
      setMsg('OS preventiva gerada.');
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    } finally {
      setGerando(null);
    }
  }

  // "Pendente" = vencida ou vence nos próximos 30 dias. É o recorte de quem
  // precisa de OS agora; o cronograma inteiro só poluiria a decisão.
  const filtradas = useMemo(() => {
    const termo = buscaEquipamento.trim().toLowerCase();
    const corte = (() => {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      return d.toISOString().slice(0, 10);
    })();
    return programacoes.filter((pr) => {
      // Equipamento desativado sai do cronograma: gerar OS preventiva para um
      // aparelho que ja foi retirado nao faz sentido. O plano guarda o historico.
      if ((pr.ativo_status || 'ativo') !== 'ativo') return false;
      if (soPendentes && (pr.proxima_execucao || '').slice(0, 10) > corte) return false;
      if (termo && ![pr.ativo_nome, pr.ambiente_nome, pr.cliente_nome]
        .some((v) => (v || '').toLowerCase().includes(termo))) return false;
      return true;
    });
  }, [programacoes, soPendentes, buscaEquipamento]);

  // Uma linha por EQUIPAMENTO, nao por atividade. O cronograma tem varias
  // atividades para o mesmo aparelho (filtro, bandeja, fluido, dutos); listar cada
  // uma repetia o equipamento 4x e obrigava o usuario a escolher qual gerar.
  //
  // A linha usa a atividade mais atrasada, e o botao gera a OS dela. A atividade
  // continua indo na descricao da OS (pmocCriarOsDaProgramacao monta
  // "PMOC - <atividade>"), so nao e mais uma decisao da tela.
  const grupos = useMemo(() => {
    const porAtivo = new Map<number, Programacao[]>();
    filtradas.forEach((pr) => {
      const lista = porAtivo.get(pr.ativo_id) || [];
      lista.push(pr);
      porAtivo.set(pr.ativo_id, lista);
    });
    const saida = Array.from(porAtivo.values()).map((lista) => {
      const ordenadas = [...lista].sort((a, b) =>
        (a.proxima_execucao || '').localeCompare(b.proxima_execucao || ''));
      // `atividades` guarda o cronograma inteiro do equipamento: a linha mostra o
      // resumo e, ao clicar, abre atividade por atividade com o botao de cada uma.
      return { proxima: ordenadas[0], pendentes: ordenadas.length, atividades: ordenadas };
    });
    return saida.sort((a, b) =>
      (a.proxima.proxima_execucao || '').localeCompare(b.proxima.proxima_execucao || ''));
  }, [filtradas]);

  const totalPaginas = Math.max(1, Math.ceil(grupos.length / ITENS_POR_PAGINA));
  const paginaSegura = Math.min(pagina, totalPaginas);
  const visiveis = grupos.slice((paginaSegura - 1) * ITENS_POR_PAGINA, paginaSegura * ITENS_POR_PAGINA);

  // Mudar filtro com a lista curta deixaria o usuário numa página que não existe mais.
  useEffect(() => { setPagina(1); }, [buscaEquipamento, soPendentes]);
  useEffect(() => { if (pagina > totalPaginas) setPagina(totalPaginas); }, [pagina, totalPaginas]);

  return (
    <div className="card">
      <div className="titulo-pg" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Cronograma — gerar OS preventiva</h3>
        <button className="btn sec sm" onClick={carregar} disabled={carregando}>
          {carregando ? 'Carregando...' : 'Atualizar'}
        </button>
      </div>

      <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
        Uma linha por <strong>equipamento ativo</strong> com manutenção programada. Equipamento desativado
        não aparece aqui. <strong>Gerar OS</strong> cria a ordem de serviço
        preventiva dele — ela nasce ligada ao plano, então entra como <strong>parada planejada</strong> e não
        conta como falha no MTTR/MTBF.
      </p>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <label>Filtrar por equipamento ou cliente</label>
      <input
        className="input"
        value={buscaEquipamento}
        onChange={(e) => setBuscaEquipamento(e.target.value)}
        placeholder="Nome do equipamento, ambiente ou cliente"
      />

      <label className="check-line" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />
        <span>Só o que está vencido ou vence em 30 dias ({grupos.length} equipamento(s) ativo(s))</span>
      </label>

      <div className="tabela-wrap" style={{ marginTop: 12 }}>
        <table>
          <thead>
            <tr>
              <th>Equipamento</th>
              <th>Cliente</th>
              <th>Plano</th>
              <th className="num">Próxima</th>
              <th>Situação</th>
              <th>Última OS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map(({ proxima: pr, pendentes, atividades }) => [
              <tr
                key={pr.ativo_id}
                style={pendentes > 1 ? { cursor: 'pointer' } : undefined}
                onClick={pendentes > 1 ? () => setAberto(aberto === pr.ativo_id ? null : pr.ativo_id) : undefined}
              >
                <td>
                  <strong>{pendentes > 1 ? (aberto === pr.ativo_id ? '▾ ' : '▸ ') : ''}{pr.ativo_nome}</strong>
                  {pr.ambiente_nome && <><br /><small>{pr.ambiente_nome}</small></>}
                  {pendentes > 1 && <><br /><small>{pendentes} manutenções programadas — clique para ver</small></>}
                </td>
                <td>{pr.cliente_nome || <span style={{ color: 'var(--texto-suave)' }}>sem cliente</span>}</td>
                <td>{pr.plano_nome}</td>
                <td className="num">{dataBR(pr.proxima_execucao)}</td>
                <td>{pr.status_calculado || pr.status}</td>
                <td>{pr.ultima_os_codigo ? pr.ultima_os_codigo + (pr.os_status ? ' (' + pr.os_status + ')' : '') : '—'}</td>
                <td className="acoes">
                  <button
                    className="btn sm"
                    disabled={gerando === pr.id}
                    onClick={(e) => { e.stopPropagation(); gerarOs(pr.id); }}
                  >
                    {gerando === pr.id ? 'Gerando...' : 'Gerar OS'}
                  </button>
                </td>
              </tr>,
              aberto === pr.ativo_id ? (
                <tr key={pr.ativo_id + '-cron'}>
                  <td colSpan={7}>
                    <table>
                      <thead>
                        <tr><th>Manutenção programada</th><th className="num">A cada</th><th>Norma</th><th className="num">Próxima</th><th></th></tr>
                      </thead>
                      <tbody>
                        {atividades.map((a) => (
                          <tr key={a.id}>
                            <td>{a.atividade_nome}</td>
                            <td className="num">{a.periodicidade_dias} dia(s)</td>
                            <td>{a.norma_referencia || '—'}</td>
                            <td className="num">{dataBR(a.proxima_execucao)}</td>
                            <td className="acoes">
                              <button className="btn sec sm" disabled={gerando === a.id} onClick={() => gerarOs(a.id)}>
                                {gerando === a.id ? 'Gerando...' : 'Gerar OS'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </td>
                </tr>
              ) : null,
            ])}
            {!visiveis.length && (
              <tr>
                <td colSpan={7} className="vazio">
                  {carregando
                    ? 'Carregando...'
                    : (!programacoes.length
                      ? 'Nenhum cronograma. Ele é criado no passo Cronograma do plano PMOC, na ficha do cliente.'
                      : (buscaEquipamento.trim()
                        ? 'Nenhuma atividade para esse equipamento.'
                        : 'Nada vencendo nos próximos 30 dias. Desmarque acima para ver o cronograma inteiro.'))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPaginas > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button className="btn sec sm" disabled={paginaSegura <= 1} onClick={() => setPagina(paginaSegura - 1)}>
            Anterior
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            Página {paginaSegura} de {totalPaginas} · {grupos.length} equipamento(s)
          </span>
          <button className="btn sec sm" disabled={paginaSegura >= totalPaginas} onClick={() => setPagina(paginaSegura + 1)}>
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}
